import { useForm, SubmitHandler } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import Lottie from 'lottie-react';
import React, { useState, useRef, useEffect } from 'react';

import { AppDispatch, RootState } from '../../../core/data/redux/store';
import { updateBookingStatus } from '../../../core/data/redux/action';
import Loader from '../../../core/data/img/car_loading.json';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import ImageSlider from '../../../feature-module/common/uploadedImage';

import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import type { Slide } from 'yet-another-react-lightbox';

const Checkout = ({ booking }: { booking: any }) => {
  interface Attachment {
    file: File;
    name: string;
    label: string;
    type: 'upload';
  }

  type FormValues = {
    attachments: any;
    odometer_reading: string;
    key_handover: boolean;
    damages: boolean;
    fuel_level: string;
  };

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [exteriorFiles, setExteriorFiles] = useState<Attachment[]>([]);
  const [interiorFiles, setInteriorFiles] = useState<Attachment[]>([]);
  const [fuelFiles, setFuelFiles] = useState<Attachment[]>([]);
  const [damageFiles, setDamageFiles] = useState<Attachment[]>([]);

  const [isMobile] = useState(window.innerWidth <= 767);
  const [isCameraOpen, setIsCameraOpen] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [Error, setError] = useState('');
  const dispatch = useDispatch<AppDispatch>();

  const { bookingsLoading, user } = useSelector((state: RootState) => ({
    user: state.auth.user,
    bookingsLoading: state.booking.bookingsLoading,
  }));

  const openCamera = async (option: string) => {
    setIsCameraOpen(option);
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: 'environment',
        },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      setError(
        'Permission denied when accessing the camera. Please enable camera access in device settings.'
      );
      console.error('Error accessing camera:', error);
    }
  };

  const handleSelfieCapture = (
    event: React.ChangeEvent<HTMLInputElement>,
    option: string
  ) => {
    const files = event.target.files;
    if (files) {
      const newAttachments: Attachment[] = Array.from(files).map((file) => ({
        file: file,
        name: file.name,
        label:
          option === 'exterior'
            ? 'interior_photo'
            : option === 'interior'
              ? 'exterior_photo'
              : option === 'fuel'
                ? 'fuel_level_photo'
                : option === 'damage'
                  ? 'damage_photo'
                  : '',
        type: 'upload',
      }));

      if (option === 'exterior') {
        setExteriorFiles((prevFiles) => [...prevFiles, ...newAttachments]);
      } else if (option === 'interior') {
        setInteriorFiles((prevFiles) => [...prevFiles, ...newAttachments]);
      } else if (option === 'fuel') {
        setFuelFiles((prevFiles) => [...prevFiles, ...newAttachments]);
      } else if (option === 'damage') {
        setDamageFiles((prevFiles) => [...prevFiles, ...newAttachments]);
      }
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsCameraOpen(''); // Or false if it's a boolean
  };

  const schema = Yup.object().shape({
    odometer_reading: Yup.string().required('Odometer Readingis required'),
    key_handover: Yup.boolean().oneOf([true], 'Key Handover is required'), //
    fuel_level: Yup.string().required('Fuel Level is required'),
    damages: Yup.boolean().notRequired(),
    attachments: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required('Attachment label is required'),
          type: Yup.string().required('Attachment type is required'),
        })
      )
      .min(1, 'At least one attachment is required')
      .required('Attachments are required')
      .test(
        'required-labels-types',
        'Attachments must include: interior_photo (upload), exterior_photo (upload), fuel_level_photo (upload)',
        function (attachments) {
          const requiredAttachments = [
            { label: 'interior_photo', type: 'upload' },
            { label: 'exterior_photo', type: 'upload' },
            { label: 'fuel_level_photo', type: 'upload' },
          ];

          // Check if each required attachment exists
          const hasAllRequired = requiredAttachments.every((required) =>
            attachments?.some(
              (att) =>
                att.label?.toLowerCase() === required.label &&
                att.type?.toLowerCase() === required.type
            )
          );

          return hasAllRequired;
        }
      ),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitted },
    reset,
    setValue,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      attachments: [],
      odometer_reading: '',
      fuel_level: '',
    },
  });

  const attachments: Attachment[] = watch('attachments') || [];

  const clearForm = () => {
    reset();
    setExteriorFiles([]);
    setInteriorFiles([]);
    setFuelFiles([]);
    setDamageFiles([]);
  };

  useEffect(() => {
    clearForm();
  }, [booking]);

  useEffect(() => {
    const modalElement = document.getElementById('checkout');

    const handleModalClose = () => {
      clearForm();
    };

    modalElement?.addEventListener('hidden.bs.modal', handleModalClose);

    return () => {
      modalElement?.removeEventListener('hidden.bs.modal', handleModalClose);
    };
  }, []);

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const formData = new FormData();

    formData.append('status_id', '6');
    formData.append('booking_id', booking.id);

    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        formData.append(key, value.toString()); // "true" or "false"
      } else if (typeof value === 'string') {
        formData.append(key, value);
      }
    });

    let fileIndex = 0;

    const appendAttachments = (files: any[]) => {
      files?.forEach((attachment) => {
        formData.append(`attachments[${fileIndex}][file]`, attachment?.file);
        formData.append(`attachments[${fileIndex}][label]`, attachment?.label);
        formData.append(`attachments[${fileIndex}][type]`, attachment?.type);
        fileIndex++;
      });
    };

    if (exteriorFiles && Array.isArray(exteriorFiles)) {
      appendAttachments(exteriorFiles);
    }

    if (interiorFiles && Array.isArray(interiorFiles)) {
      appendAttachments(interiorFiles);
    }

    if (fuelFiles && Array.isArray(fuelFiles)) {
      appendAttachments(fuelFiles);
    }

    if (damageFiles && Array.isArray(damageFiles)) {
      appendAttachments(damageFiles);
    }

    dispatch(updateBookingStatus(formData));
  };

  const removeFile = (fileName: string, option: string) => {
    if (option === 'exterior') {
      setExteriorFiles((prevFiles) =>
        prevFiles.filter((file) => file.name !== fileName)
      );
    } else if (option === 'interior') {
      setInteriorFiles((prevFiles) =>
        prevFiles.filter((file) => file.name !== fileName)
      );
    } else if (option === 'fuel') {
      setFuelFiles((prevFiles) =>
        prevFiles.filter((file) => file.name !== fileName)
      );
    } else if (option === 'damages') {
      setDamageFiles((prevFiles) =>
        prevFiles.filter((file) => file.name !== fileName)
      );
    }
  };

  const captureSelfie = (selfieFiles: any, option: string) => {
    if (videoRef.current && canvasRef.current && !Error) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (context) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob((blob) => {
          if (blob) {
            const selfieIndex = selfieFiles.length + 1;
            const fileName = `${user?.first_name}_${option}_${user?.id}_${booking?.id}_${selfieIndex}.png`;
            const selfieFile = new File([blob], fileName, {
              type: 'image/png',
            });

            const newAttachment: Attachment = {
              file: selfieFile,
              name: selfieFile.name,
              label:
                option === 'exterior'
                  ? 'exterior_photo'
                  : option === 'interior'
                    ? 'interior_photo'
                    : option === 'fuel'
                      ? 'fuel_level_photo'
                      : option === 'damages'
                        ? 'damages'
                        : '',
              type: 'upload',
            };

            if (option === 'exterior') {
              setExteriorFiles((prevFiles) => [...prevFiles, newAttachment]);
            } else if (option === 'interior') {
              setInteriorFiles((prevFiles) => [...prevFiles, newAttachment]);
            } else if (option === 'fuel') {
              setFuelFiles((prevFiles) => [...prevFiles, newAttachment]);
            } else if (option === 'damages') {
              setDamageFiles((prevFiles) => [...prevFiles, newAttachment]);
            }

            setValue('attachments', [...attachments, newAttachment], {
              shouldValidate: true,
            });
          }
        }, 'image/png');

        const stream = videoRef.current.srcObject as MediaStream;
        const tracks = stream?.getTracks();
        tracks?.forEach((track) => track.stop());

        setIsCameraOpen('');
      }
    }
  };

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [lightboxSlide, setLightboxSlide] = useState<Slide[]>([]);

  return (
    <>
      <div
        className="modal new-modal fade"
        id="checkout"
        data-keyboard="false"
        data-backdrop="static"
      >
        <div className="modal-dialog modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header mb-0">
              <h4 className="modal-title">End Rental</h4>
              <button
                type="button"
                className="close-btn"
                data-bs-dismiss="modal"
              >
                <span>×</span>
              </button>
            </div>

            <div className="modal-body">
              {bookingsLoading ? (
                <div className="d-flex justify-content-center align-items-center">
                  <Lottie
                    animationData={Loader}
                    loop={true}
                    style={{ width: 300, height: 300 }}
                  />
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row">
                    <div className="col-12 px-2 pt-1 mb-0">
                      <div className="col-12 mb-3 px-0">
                        <div className="text-center mb-2">
                          {isCameraOpen === 'exterior' && (
                            <div>
                              <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                style={{
                                  width: '100%',
                                  maxWidth: '300px',
                                  marginTop: '10px',
                                  transformOrigin: 'center center',
                                }}
                              />
                              <canvas
                                ref={canvasRef}
                                style={{ display: 'none' }}
                              />

                              <p className="text-danger">{Error}</p>
                              <button
                                type="button"
                                className="mt-2 p-2 border-0"
                                onClick={() => {
                                  captureSelfie(exteriorFiles, 'exterior');
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                📸 Capture
                              </button>
                            </div>
                          )}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            capture="user"
                            onChange={(e) => {
                              handleSelfieCapture(e, 'exterior');
                            }}
                            style={{ display: 'none' }}
                          />
                        </div>

                        <div
                          onClick={() => {
                            openCamera('exterior');
                          }}
                          style={{
                            border:
                              errors.attachments && exteriorFiles?.length === 0
                                ? '2px dashed red'
                                : exteriorFiles?.length === 0
                                  ? '2px dashed #737373'
                                  : '2px dashed #ccc',
                            boxShadow:
                              exteriorFiles?.length === 0
                                ? '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)'
                                : 'none',
                            padding: '8px',
                            cursor: 'pointer',
                            marginBottom: '10px',
                            textAlign: 'center',
                          }}
                        >
                          <p className="mb-0">
                            {isMobile
                              ? 'Click to take Exterior photo'
                              : 'Drag & drop Exterior photo , or Click to Select'}
                          </p>
                        </div>

                        {/* <ul>
                          {exteriorFiles?.map((file) => (
                            <li key={file.name} className="mb-1">
                              <img
                                src={URL.createObjectURL(file.file)}
                                width={200}
                                height={200}
                              />{' '}
                              <br />
                              {file.name}{' '}
                              <span
                                style={{ color: 'red', cursor: 'pointer' }}
                                onClick={() =>
                                  removeFile(file.name, 'exterior')
                                }
                              >
                                {' '}
                                <i className="feather icon-x-circle " />
                              </span>
                            </li>
                          ))}
                        </ul> */}

                        {exteriorFiles?.length > 0 && (
                          <ImageSlider
                            selectedFiles={exteriorFiles}
                            name="exterior"
                            removeFile={removeFile}
                            setLightboxSlide={setLightboxSlide}
                            setLightboxIndex={setLightboxIndex}
                          />
                        )}

                        <div className="text-center mb-2">
                          {isCameraOpen === 'interior' && (
                            <div>
                              <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                style={{
                                  width: '100%',
                                  maxWidth: '300px',
                                  marginTop: '10px',
                                  transformOrigin: 'center center',
                                }}
                              />
                              <canvas
                                ref={canvasRef}
                                style={{ display: 'none' }}
                              />

                              <p className="text-danger">{Error}</p>
                              <button
                                type="button"
                                className="mt-2 p-2 border-0"
                                onClick={() => {
                                  captureSelfie(interiorFiles, 'interior');
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                📸 Capture
                              </button>
                            </div>
                          )}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            capture="user"
                            onChange={(e) => {
                              handleSelfieCapture(e, 'interior');
                            }}
                            style={{ display: 'none' }}
                          />
                        </div>

                        <div
                          onClick={() => {
                            openCamera('interior');
                          }}
                          style={{
                            border:
                              errors.attachments && interiorFiles?.length === 0
                                ? '2px dashed red'
                                : exteriorFiles?.length > 0 &&
                                    interiorFiles?.length === 0
                                  ? '2px dashed #737373'
                                  : '2px dashed #ccc',
                            boxShadow:
                              exteriorFiles?.length > 0 &&
                              interiorFiles?.length === 0
                                ? '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)'
                                : 'none',
                            padding: '8px',
                            cursor: 'pointer',
                            marginBottom: '10px',
                            textAlign: 'center',
                          }}
                        >
                          <p className="mb-0">
                            {isMobile
                              ? 'Click to take Interior photo'
                              : 'Drag & drop Interior photo , or Click to Select'}
                          </p>
                        </div>

                        {/* <ul>
                          {interiorFiles?.map((file) => (
                            <li key={file.name} className="mb-1">
                              <img
                                src={URL.createObjectURL(file.file)}
                                width={200}
                                height={200}
                              />{' '}
                              <br />
                              {file.name}{' '}
                              <span
                                style={{ color: 'red', cursor: 'pointer' }}
                                onClick={() =>
                                  removeFile(file.name, 'interior')
                                }
                              >
                                {' '}
                                <i className="feather icon-x-circle " />
                              </span>
                            </li>
                          ))}
                        </ul> */}

                        {interiorFiles?.length > 0 && (
                          <ImageSlider
                            selectedFiles={interiorFiles}
                            name="interior"
                            removeFile={removeFile}
                            setLightboxSlide={setLightboxSlide}
                            setLightboxIndex={setLightboxIndex}
                          />
                        )}

                        <div className="text-center mb-2">
                          {isCameraOpen === 'fuel' && (
                            <div>
                              <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                style={{
                                  width: '100%',
                                  maxWidth: '300px',
                                  marginTop: '10px',
                                  transformOrigin: 'center center',
                                }}
                              />
                              <canvas
                                ref={canvasRef}
                                style={{ display: 'none' }}
                              />

                              <p className="text-danger">{Error}</p>
                              <button
                                type="button"
                                className="mt-2 p-2 border-0"
                                onClick={() => {
                                  captureSelfie(interiorFiles, 'fuel');
                                }}
                                style={{ cursor: 'pointer' }}
                              >
                                📸 Capture
                              </button>
                            </div>
                          )}
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            capture="user"
                            onChange={(e) => {
                              handleSelfieCapture(e, 'fuel');
                            }}
                            style={{ display: 'none' }}
                          />
                        </div>

                        <div
                          onClick={() => {
                            openCamera('fuel');
                          }}
                          style={{
                            border:
                              errors.attachments && fuelFiles?.length === 0
                                ? '2px dashed red'
                                : exteriorFiles?.length > 0 &&
                                    interiorFiles?.length > 0 &&
                                    fuelFiles?.length === 0
                                  ? '2px dashed #737373'
                                  : '2px dashed #ccc',
                            boxShadow:
                              exteriorFiles?.length > 0 &&
                              interiorFiles?.length > 0 &&
                              fuelFiles?.length === 0
                                ? '0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)'
                                : 'none',
                            padding: '8px',
                            cursor: 'pointer',
                            marginBottom: '10px',
                            textAlign: 'center',
                          }}
                        >
                          <p className="mb-0">
                            {isMobile
                              ? 'Click to take Fuel photo'
                              : 'Drag & drop Fuel photo , or Click to Select'}
                          </p>
                        </div>

                        {/* <ul>
                          {fuelFiles?.map((file) => (
                            <li key={file.name} className="mb-1">
                              <img
                                src={URL.createObjectURL(file.file)}
                                width={200}
                                height={200}
                              />{' '}
                              <br />
                              {file.name}{' '}
                              <span
                                style={{ color: 'red', cursor: 'pointer' }}
                                onClick={() => removeFile(file.name, 'fuel')}
                              >
                                {' '}
                                <i className="feather icon-x-circle " />
                              </span>
                            </li>
                          ))}
                        </ul>
                         */}

                        {fuelFiles?.length > 0 && (
                          <ImageSlider
                            selectedFiles={fuelFiles}
                            name="fuel"
                            removeFile={removeFile}
                            setLightboxSlide={setLightboxSlide}
                            setLightboxIndex={setLightboxIndex}
                          />
                        )}

                        <div className="row mb-1">
                          <div className="col-6">
                            <input
                              type="number"
                              className={`form-control  ${isSubmitted && errors?.odometer_reading ? 'is-invalid' : ''} mb-0`}
                              placeholder="Odometer Reading"
                              {...register('odometer_reading', {
                                required: 'Is required',
                              })}
                            />
                          </div>

                          <div className="col-6 d-flex align-items-center justify-content-end gap-1">
                            <div className="input-block m-0">
                              <label className="custom_check d-inline-flex location-check m-0">
                                Key Handover
                                <input
                                  type="checkbox"
                                  className={`form-control mb-0 ${isSubmitted && errors?.key_handover ? 'is-invalid' : ''}`}
                                  {...register('key_handover', {
                                    required: 'Is required',
                                  })}
                                />
                                <span
                                  style={{ padding: 5 }}
                                  className={`checkmark form-control  ${isSubmitted && errors?.key_handover ? 'is-invalid' : ''}`}
                                />
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className="row mb-1">
                          <div className="col-6">
                            <select
                              className={`form-select fs-13  ${isSubmitted && errors?.fuel_level ? 'is-invalid' : ''} `}
                              aria-label="Default select example"
                              {...register('fuel_level', {
                                required: 'Is required',
                              })}
                            >
                              <option value="" className="fs-12">
                                Select Fuel Level
                              </option>
                              {['1/4', '1/2', '3/4', 'full'].map(
                                (item, index) => (
                                  <option
                                    key={index}
                                    value={item}
                                    className="fs-12"
                                  >
                                    {item}
                                  </option>
                                )
                              )}
                            </select>
                          </div>

                          <div className="col-6 d-flex align-items-center  justify-content-end gap-1">
                            <div className="input-block m-0">
                              <label className="custom_check d-inline-flex location-check m-0">
                                Damages
                                <input
                                  type="checkbox"
                                  className={`form-control mb-0 ${isSubmitted && errors?.damages ? 'is-invalid' : ''}`}
                                  {...register('damages', {
                                    required: 'Is required',
                                  })}
                                />
                                <span
                                  style={{ padding: 5 }}
                                  className={`checkmark form-control  ${isSubmitted && errors?.damages ? 'is-invalid' : ''}`}
                                />
                              </label>
                            </div>
                          </div>
                        </div>

                        {watch('damages') && (
                          <>
                            <div className="text-center mb-2">
                              {isCameraOpen === 'damages' && (
                                <div>
                                  <video
                                    ref={videoRef}
                                    autoPlay
                                    playsInline
                                    style={{
                                      width: '100%',
                                      maxWidth: '300px',
                                      marginTop: '10px',
                                      transformOrigin: 'center center',
                                    }}
                                  />
                                  <canvas
                                    ref={canvasRef}
                                    style={{ display: 'none' }}
                                  />

                                  <p className="text-danger">{Error}</p>
                                  <button
                                    type="button"
                                    className="mt-2 p-2 border-0"
                                    onClick={() => {
                                      captureSelfie(interiorFiles, 'damages');
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    📸 Capture
                                  </button>
                                </div>
                              )}
                              <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                capture="user"
                                onChange={(e) => {
                                  handleSelfieCapture(e, 'damages');
                                }}
                                style={{ display: 'none' }}
                              />
                            </div>

                            <div
                              onClick={() => {
                                openCamera('damages');
                              }}
                              style={{
                                border: '2px dashed #ccc',
                                boxShadow: 'none',
                                padding: '8px',
                                cursor: 'pointer',
                                marginBottom: '10px',
                                textAlign: 'center',
                              }}
                            >
                              <p className="mb-0">
                                {isMobile
                                  ? 'Click to take damage photo'
                                  : 'Drag & drop damage photo , or Click to Select'}
                              </p>
                            </div>

                            {/* <ul>
                              {damageFiles?.map((file) => (
                                <li key={file.name} className="mb-1">
                                  <img
                                    src={URL.createObjectURL(file.file)}
                                    width={200}
                                    height={200}
                                  />{' '}
                                  <br />
                                  {file.name}{' '}
                                  <span
                                    style={{ color: 'red', cursor: 'pointer' }}
                                    onClick={() =>
                                      removeFile(file.name, 'damages')
                                    }
                                  >
                                    {' '}
                                    <i className="feather icon-x-circle " />
                                  </span>
                                </li>
                              ))}
                            </ul> */}

                            {damageFiles?.length > 0 && (
                              <ImageSlider
                                selectedFiles={damageFiles}
                                name="damages"
                                removeFile={removeFile}
                                setLightboxSlide={setLightboxSlide}
                                setLightboxIndex={setLightboxIndex}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="modal-btn modal-btn-sm">
                    <button
                      type="submit"
                      className="btn btn-primary w-100 btn-size"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              )}

              <Lightbox
                plugins={[Captions, Fullscreen, Zoom]}
                open={lightboxIndex !== null}
                close={() => setLightboxIndex(null)}
                index={lightboxIndex ?? 0}
                slides={lightboxSlide}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkout;
